// Fix for apolloClient.ts
import { ApolloClient, from, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getAuth, onAuthStateChanged, type Auth } from 'firebase/auth';
import * as Sentry from '@sentry/react';
import config from './config';

// On a hard refresh Firebase restores the persisted user asynchronously, so
// `auth.currentUser` is null for the first few hundred ms after page load. If a
// query fires in that window it goes out with no token and the backend treats it
// as unauthenticated (e.g. a private watch 403s → "no access" + redirect). Wait
// for the first auth-state resolution (which fires for signed-in AND signed-out
// users once persistence is restored) before attaching the token.
let authReadyPromise: Promise<void> | null = null;
function waitForAuthReady(auth: Auth): Promise<void> {
  if (auth.currentUser) return Promise.resolve();
  if (!authReadyPromise) {
    authReadyPromise = new Promise<void>((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const unsubscribe = onAuthStateChanged(auth, () => {
        unsubscribe();
        done();
      });
      // Safety net so a never-authenticated session can't hang forever.
      setTimeout(done, 3000);
    });
  }
  return authReadyPromise;
}

// Define your GraphQL types for type policies
const typePolicies = {
  Query: {
    fields: {
      user: {
        read(existing: any) {
          return existing;
        },
        merge(existing: any, incoming: any) {
          // Cache merge for user
          return incoming; // Simply replace with incoming data
        },
      },
    },
  },
  User: {
    keyFields: ['id'],
  },
  Date: {
    fields: {
      viewedAt: {
        read(value: string | null | undefined) {
          // Convert string to Date when reading from cache
          return value ? new Date(value) : null;
        },
      },
    },
  },
};

// Debug link to log all requests and responses - keep in case we need at some point
// const debugLink = new ApolloLink((operation: Operation, forward) => {
//   // Get the auth token and print part of it
//   const context = operation.getContext();
//   const authHeader = context.headers?.authorization;
//   if (authHeader) {
//     console.log('Auth header present:', authHeader.substring(0, 20) + '...');
//   } else {
//     console.log('No auth header found in operation context');
//   }

//   return forward(operation).map((result) => {
//     console.log(`Apollo result for ${operation.operationName}:`, result);
//     return result;
//   });
// });

// Error handling link with detailed logging
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, response }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        // Log to console for debugging
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}, Operation: ${operation.operationName}`
        );
        console.error('Extensions:', extensions);

        // Only capture to Sentry if it's not a user-friendly error
        const isUserFriendlyError = extensions && 'userMessage' in extensions;
        if (!isUserFriendlyError) {
          // Capture GraphQL error with Sentry
          Sentry.captureException(new Error(`GraphQL Error: ${message}`), {
            tags: {
              type: 'graphql_error',
              operation: operation.operationName,
            },
            extra: {
              path,
              locations,
              extensions,
              operationName: operation.operationName,
              variables: operation.variables,
            },
          });
        }
      });
    }

    if (networkError) {
      console.error(
        `[Network error for ${operation.operationName}]:`,
        networkError
      );

      // Capture Network error with Sentry
      Sentry.captureException(networkError, {
        tags: {
          type: 'network_error',
          operation: operation.operationName,
        },
        extra: {
          operationName: operation.operationName,
          variables: operation.variables,
        },
      });
    }

    // Log the partial response if available
    if (response) {
      console.error('Partial response:', response);
    }
  }
);

// Create the auth link to add Firebase token to requests
const authLink = setContext(async (_, { headers }) => {
  // Get the current Firebase user (waiting for persistence to restore on a refresh)
  const auth = getAuth();
  await waitForAuthReady(auth);
  const user = auth.currentUser;

  // If there's a user, get their token
  let token = '';
  if (user) {
    try {
      token = await user.getIdToken();
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
  }

  // IMPORTANT: Always return headers object, even if empty
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Use the correct GraphQL endpoint
// REACT_APP_API_URL should be set to the base API path (e.g., https://agentwatch.helivan.io/api)
// In development, use localhost; in production, REACT_APP_API_URL must be set
const getGraphQLUri = () => {
  if (config.reactApp.apiUrl) {
    return `${config.reactApp.apiUrl}/graphql`;
  }
  // Development fallback - check if we're on localhost
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:4000/api/graphql';
  }
  // Production without config - this should not happen, log error
  console.error('REACT_APP_API_URL is not configured. API calls will fail.');
  return '/api/graphql'; // Relative URL as last resort
};

const httpLink = new HttpLink({
  uri: getGraphQLUri(),
  fetchOptions: {
    mode: 'cors',
    credentials: 'same-origin',
    timeout: 60000,
  },
});

const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies,
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  },
});

export default client;
